import {
  IdentifierTypeApiError,
  IdentifierTypeCreateCommand,
  IdentifierTypeInUseViewModel,
  IdentifierTypeViewModel,
  JuridictionIdentifierTypeEnum
} from '../../../models/Entities/Person/IdentifierType/IdentifierTypeModels';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';

/**
 * Funciones puras de los tipos de identificador (spec 011). El nombre "CUIT" es el que buscan
 * MedicalProtocols.API (`ProviderImport:TaxIdIdentifierTypeName`) y Persons.API
 * (`IdentifierTypeResolver`) para el CUIT de los proveedores: sin mayúsculas ni espacios en los
 * extremos; si hay más de uno, la búsqueda falla por ambigüedad (422).
 */

export const CUIT_IDENTIFIER_TYPE_NAME = 'CUIT';

/** Límites alineados con los validadores de Persons.API (spec 005; DEC-011-02, DEC-011-06). Se cuentan recortados. */
export const IDENTIFIER_TYPE_NAME_MAX_LENGTH = 100;
export const IDENTIFIER_TYPE_DESCRIPTION_MAX_LENGTH = 500;
export const IDENTIFIER_TYPE_MASK_MAX_LENGTH = 100;
export const IDENTIFIER_TYPE_REGEX_MAX_LENGTH = 500;

/** Datos precargados de "Crear CUIT" (DEC-011-04). */
export const CUIT_PREFILL: IdentifierTypeCreateCommand = {
  name: CUIT_IDENTIFIER_TYPE_NAME,
  description: 'Clave Única de Identificación Tributaria',
  juridictionType: JuridictionIdentifierTypeEnum.National,
  isForNaturalPerson: true,
  isForJuridicalPerson: true,
  identifierMask: '##-########-#',
  validationRegex: '^\\d{2}-?\\d{8}-?\\d$',
  isActive: true
};

/** Comparación de nombres igual a la del servidor: sin espacios en los extremos y sin mayúsculas. */
export const normalizeName = (name: string | null | undefined): string => (name ?? '').trim().toLowerCase();

/** Texto de búsqueda: además sin acentos. */
const normalizeSearch = (value: string | null | undefined): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/**
 * "Persona física": `isForNaturalPerson` (Persons.API spec 005). El alias obsoleto `isForLegalPerson` sólo se
 * lee si el servidor todavía no manda el campo nuevo.
 */
export const isForNaturalPersonOf = (item: Pick<IdentifierTypeViewModel, 'isForNaturalPerson' | 'isForLegalPerson'>): boolean =>
  item.isForNaturalPerson ?? item.isForLegalPerson ?? false;

/** Tipos no eliminados. */
export const visibleIdentifierTypes = (items: IdentifierTypeViewModel[] | undefined): IdentifierTypeViewModel[] =>
  (items ?? []).filter((item) => !item.isDeleted);

/** Tipos no eliminados llamados "CUIT". */
export const findCuitTypes = (items: IdentifierTypeViewModel[] | undefined): IdentifierTypeViewModel[] =>
  visibleIdentifierTypes(items).filter((item) => normalizeName(item.name) === normalizeName(CUIT_IDENTIFIER_TYPE_NAME));

/** El tipo es el del CUIT de proveedores. */
export const isCuitName = (name: string | null | undefined): boolean =>
  normalizeName(name) === normalizeName(CUIT_IDENTIFIER_TYPE_NAME);

/** Filtra por nombre o descripción (sin mayúsculas ni acentos). */
export const filterIdentifierTypes = (items: IdentifierTypeViewModel[], search: string): IdentifierTypeViewModel[] => {
  const needle = normalizeSearch(search);
  if (!needle) return items;
  return items.filter((item) => [item.name, item.description].some((value) => normalizeSearch(value).includes(needle)));
};

/** Valores del formulario. */
export interface IdentifierTypeFormValues {
  name: string;
  description: string;
  juridictionType: JuridictionIdentifierTypeEnum;
  isForNaturalPerson: boolean;
  isForJuridicalPerson: boolean;
  identifierMask: string;
  validationRegex: string;
  isActive: boolean;
}

export const EMPTY_FORM_VALUES: IdentifierTypeFormValues = {
  name: '',
  description: '',
  juridictionType: JuridictionIdentifierTypeEnum.National,
  isForNaturalPerson: true,
  isForJuridicalPerson: false,
  identifierMask: '',
  validationRegex: '',
  isActive: true
};

/** Valores iniciales: el tipo a editar, el prefill (Crear CUIT) o vacío. */
export const formValuesOf = (
  item?: IdentifierTypeViewModel,
  prefill?: Partial<IdentifierTypeCreateCommand>
): IdentifierTypeFormValues => {
  if (item) {
    return {
      name: item.name ?? '',
      description: item.description ?? '',
      juridictionType: item.juridictionType ?? JuridictionIdentifierTypeEnum.National,
      isForNaturalPerson: isForNaturalPersonOf(item),
      isForJuridicalPerson: !!item.isForJuridicalPerson,
      identifierMask: item.identifierMask ?? '',
      validationRegex: item.validationRegex ?? '',
      isActive: item.isActive ?? true
    };
  }
  if (prefill) {
    return {
      ...EMPTY_FORM_VALUES,
      name: prefill.name ?? '',
      description: prefill.description ?? '',
      juridictionType: prefill.juridictionType ?? EMPTY_FORM_VALUES.juridictionType,
      isForNaturalPerson: prefill.isForNaturalPerson ?? EMPTY_FORM_VALUES.isForNaturalPerson,
      isForJuridicalPerson: prefill.isForJuridicalPerson ?? EMPTY_FORM_VALUES.isForJuridicalPerson,
      identifierMask: prefill.identifierMask ?? '',
      validationRegex: prefill.validationRegex ?? '',
      isActive: prefill.isActive ?? true
    };
  }
  return { ...EMPTY_FORM_VALUES };
};

export interface IdentifierTypeValidation {
  name?: 'required' | 'tooLong' | 'duplicate';
  description?: 'tooLong';
  identifierMask?: 'tooLong';
  personType?: 'personType';
  validationRegex?: 'regex' | 'tooLong';
}

/**
 * La expresión compila con `RegExp` de JavaScript. El servidor la valida con `Regex` de .NET: en lo habitual
 * coinciden, pero un patrón con sintaxis exclusiva de un motor puede pasar acá y volver con 400 (se muestra el
 * mensaje del servidor en el campo) o al revés.
 */
export const isRegexValid = (pattern: string): boolean => {
  if (!pattern.trim()) return true;
  try {
    // eslint-disable-next-line no-new
    new RegExp(pattern.trim());
    return true;
  } catch {
    return false;
  }
};

/** Valida el formulario; `existing` son los tipos cargados (se excluye el propio al editar). */
export const validateIdentifierType = (
  values: IdentifierTypeFormValues,
  existing: IdentifierTypeViewModel[],
  editingId?: number
): IdentifierTypeValidation => {
  const result: IdentifierTypeValidation = {};
  const name = values.name.trim();
  if (!name) result.name = 'required';
  else if (name.length > IDENTIFIER_TYPE_NAME_MAX_LENGTH) result.name = 'tooLong';
  else if (
    visibleIdentifierTypes(existing).some((item) => item.id !== editingId && normalizeName(item.name) === normalizeName(name))
  )
    result.name = 'duplicate';
  if (values.description.trim().length > IDENTIFIER_TYPE_DESCRIPTION_MAX_LENGTH) result.description = 'tooLong';
  if (values.identifierMask.trim().length > IDENTIFIER_TYPE_MASK_MAX_LENGTH) result.identifierMask = 'tooLong';
  if (!values.isForNaturalPerson && !values.isForJuridicalPerson) result.personType = 'personType';
  if (values.validationRegex.trim().length > IDENTIFIER_TYPE_REGEX_MAX_LENGTH) result.validationRegex = 'tooLong';
  else if (!isRegexValid(values.validationRegex)) result.validationRegex = 'regex';
  return result;
};

export const hasErrors = (validation: IdentifierTypeValidation): boolean => Object.keys(validation).length > 0;

const emptyToNull = (value: string): string | null => (value.trim() ? value.trim() : null);

/** Comando del POST / PUT: textos recortados y vacíos como null. */
export const toCommand = (values: IdentifierTypeFormValues): IdentifierTypeCreateCommand => ({
  name: values.name.trim(),
  description: emptyToNull(values.description),
  juridictionType: values.juridictionType,
  isForNaturalPerson: values.isForNaturalPerson,
  isForJuridicalPerson: values.isForJuridicalPerson,
  identifierMask: emptyToNull(values.identifierMask),
  validationRegex: emptyToNull(values.validationRegex),
  isActive: values.isActive
});

/** Comando del PUT para activar / desactivar: los datos del tipo con `isActive` cambiado. */
export const toStatusCommand = (item: IdentifierTypeViewModel, isActive: boolean) => ({
  ...toCommand({ ...formValuesOf(item), isActive }),
  id: item.id
});

// ---- errores del servidor (Persons.API spec 005) ----

/** Qué pasó: 409 nombre repetido / en uso (baja), 404 el tipo ya no existe, 400 reglas de forma. */
export type IdentifierTypeErrorKind = 'duplicate' | 'inUse' | 'notFound' | 'validation' | 'other';

const apiErrorOf = (error: unknown): Partial<IdentifierTypeApiError> =>
  typeof error === 'object' && error !== null ? (error as Partial<IdentifierTypeApiError>) : {};

/** Identificadores que usan el tipo (`data.identifiersCount` del 409 de la baja). */
export const inUseCountOf = (error: unknown): number | undefined => {
  const data = apiErrorOf(error).Data as Partial<IdentifierTypeInUseViewModel> | undefined;
  return typeof data?.identifiersCount === 'number' ? data.identifiersCount : undefined;
};

export const identifierTypeErrorKind = (error: unknown): IdentifierTypeErrorKind => {
  const code = apiErrorOf(error).ErrorCode;
  if (code === 409) return inUseCountOf(error) !== undefined ? 'inUse' : 'duplicate';
  if (code === 404) return 'notFound';
  if (code === 400) return 'validation';
  return 'other';
};

/** Texto del error: el `message` del servidor (el genérico lo pisa en los 404) o los `errors[]`. */
export const identifierTypeErrorMessage = (error: unknown): string =>
  apiErrorOf(error).ServerMessage || getApiErrorMessage(error);

/** Campos del formulario a los que se puede asignar un error del servidor. */
export type IdentifierTypeServerField =
  | 'name'
  | 'description'
  | 'juridictionType'
  | 'personType'
  | 'identifierMask'
  | 'validationRegex';

export interface IdentifierTypeServerErrors {
  fields: Partial<Record<IdentifierTypeServerField, string>>;
  /** Los que no corresponden a un campo. */
  other: string[];
}

// Mensajes de los validadores de Persons.API (spec 005, "Reglas de forma"); el orden importa.
const SERVER_FIELD_PATTERNS: [IdentifierTypeServerField, RegExp][] = [
  ['validationRegex', /expresi[oó]n regular|regex/i],
  ['identifierMask', /m[aá]scara|mask/i],
  ['description', /descripci[oó]n|description/i],
  ['personType', /persona f[ií]sica|jur[ií]dica|natural person|juridical person/i],
  ['juridictionType', /jurisdicci[oó]n|juridiction/i],
  ['name', /\bnombre\b|\bname\b/i]
];

/** Reparte los `errors[]` de un 400 entre los campos del formulario. */
export const serverErrorsByField = (errors: string[] | undefined): IdentifierTypeServerErrors => {
  const result: IdentifierTypeServerErrors = { fields: {}, other: [] };
  (errors ?? []).forEach((message) => {
    const match = SERVER_FIELD_PATTERNS.find(([, pattern]) => pattern.test(message));
    if (!match) {
      result.other.push(message);
      return;
    }
    const field = match[0];
    result.fields[field] = result.fields[field] ? `${result.fields[field]} ${message}` : message;
  });
  return result;
};
